import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { collection, query, where, getDocs, orderBy, limit } from 'firebase/firestore';
import { db } from '../config/firebase';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../config/firebase';
import { 
  ArrowLeftIcon, 
  ArrowTopRightOnSquareIcon, 
  MagnifyingGlassIcon, 
  FunnelIcon, 
  DocumentIcon,
  CloudArrowDownIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon
} from '@heroicons/react/24/outline';
import { getFileIcon, getFileTypeText } from '../utils/fileIcons.jsx';

const FILE_CATEGORIES = [
  { id: 'all', name: 'All Categories' },
  { id: 'AdvPyD', name: 'Pay Advance' },
  { id: 'CusAgr', name: 'Contract Agreement' },
  { id: 'CusIde', name: 'Govt ID' },
  { id: 'ConPht', name: 'Coach Photo' },
  { id: 'ComRCf', name: 'Commision Received' }
];

export default function MyUploadsPage() {
  const { currentAgent } = useAuth();
  const navigate = useNavigate();
  const [uploads, setUploads] = useState([]);
  const [filteredUploads, setFilteredUploads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeFilter, setActiveFilter] = useState('all');
  const [downloadingFile, setDownloadingFile] = useState(null);

  // Load uploads
  useEffect(() => {
    const loadUploads = async () => {
      if (!currentAgent?.id) return;
      
      try {
        setLoading(true);
        const uploadsRef = collection(db, 'agent_file_uploads');
        
        // Use document ID for query (this is what's stored in agentId field)
        const agentDocId = currentAgent.id;
        
        const q = query(
          uploadsRef,
          where('agentId', '==', agentDocId),
          orderBy('uploadedAt', 'desc'),
          limit(50)
        );
        
        const querySnapshot = await getDocs(q);
        const uploadsData = [];
        
        querySnapshot.forEach((doc) => {
          const data = doc.data();
          uploadsData.push({
            id: doc.id,
            ...data
          });
        });
        
        setUploads(uploadsData);
        setFilteredUploads(uploadsData);
        setError(null);
      } catch (err) {
        console.error('Error loading uploads:', err);
        console.error('Error details:', err.message, err.code);
        setError('Failed to load uploads. Please try again later.');
      } finally {
        setLoading(false);
      }
    };
    
    loadUploads();
  }, [currentAgent?.id]);

  // Filter and search
  useEffect(() => {
    let result = [...uploads];
    
    // Apply category filter
    if (activeFilter !== 'all') {
      result = result.filter(upload => upload.category === activeFilter);
    }
    
    // Apply search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(upload => 
        upload.fileName?.toLowerCase().includes(term) ||
        upload.description?.toLowerCase().includes(term) ||
        upload.category?.toLowerCase().includes(term)
      );
    }
    
    setFilteredUploads(result);
  }, [uploads, activeFilter, searchTerm]);

  // Format date
  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  // Format file size
  const formatFileSize = (bytes) => {
    if (!bytes) return 'N/A';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
  };

  // Get category label from code
  const getCategoryLabel = (categoryCode) => {
    const category = FILE_CATEGORIES.find(c => c.id === categoryCode);
    return category ? category.name : categoryCode || 'General';
  };

  // Handle view file
  const handleViewFile = async (upload) => {
    try {
      setDownloadingFile(upload.id);
      
      // Check if running in emulator (localhost)
      const isEmulator = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
      
      if (isEmulator) {
        alert('⚠️ File viewing is not available in emulator.\n\nThe file has been uploaded to Firebase Storage.\n\nPlease deploy to production to view files.');
        setDownloadingFile(null);
        return;
      }
      
      // Build proxy URL with referrer checking
      const proxyUrl = `https://europe-west3-plsss-project.cloudfunctions.net/proxyAgentFile?agentId=${currentAgent.id}&path=${encodeURIComponent(upload.storagePath)}`;
      
      // Open in new tab - referrer will be automatically sent
      window.open(proxyUrl, '_blank');
    } catch (error) {
      console.error('View file error:', error);
      alert('Failed to open file. Please try again.');
    } finally {
      setDownloadingFile(null);
    }
  };

  // Get status badge
  const getStatusBadge = (upload) => {
    if (upload.driveStatus === 'synced') {
      return (
        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
          <CheckCircleIcon className="h-3 w-3 mr-1" />
          Synced
        </span>
      );
    } else if (upload.driveStatus === 'failed') {
      return (
        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">
          <XCircleIcon className="h-3 w-3 mr-1" />
          Sync Failed
        </span>
      );
    } else {
      return (
        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800">
          <ClockIcon className="h-3 w-3 mr-1" />
          Syncing...
        </span>
      );
    }
  };

  // Get file icon
  const renderFileIcon = (mimeType) => {
    const { Component, className, size } = getFileIcon(mimeType, 'h-5 w-5');
    return <Component className={className} size={size} />;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-6 pt-2 pb-1">
        <div className="flex items-center px-1 sm:px-0">
          <button
            onClick={() => navigate(-1)}
            className="mr-3 p-2 sm:p-1.5 rounded-full hover:bg-gray-100 active:bg-gray-200"
            aria-label="Go back"
          >
            <ArrowLeftIcon className="h-6 w-6 sm:h-5 sm:w-5 text-gray-600" />
          </button>
          <h1 className="text-lg sm:text-xl font-semibold text-gray-900">My Files</h1>
        </div>
        
        {/* Filter and Search Bar */}
        <div className="bg-white p-2 sm:p-3 rounded-lg shadow sticky top-0 z-10 mt-2 mb-3">
          <div className="flex flex-col space-y-3 sm:space-y-0 sm:flex-row sm:items-center sm:justify-between">
            {/* Search Input */}
            <div className="relative flex-1 max-w-md">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                className="block w-full pl-10 pr-3 py-2 text-base sm:text-sm border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                placeholder="Search files..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            
            {/* Category Filter */}
            <div className="flex items-start space-x-1 overflow-x-auto pb-1 sm:pb-0 -mx-1 px-1">
              <FunnelIcon className="h-5 w-5 text-gray-400 flex-shrink-0 mt-1.5 sm:mt-0" />
              <div className="flex flex-wrap gap-1.5">
                {FILE_CATEGORIES.map((category) => (
                  <button
                    key={category.id}
                    onClick={() => setActiveFilter(category.id)}
                    className={`px-3 py-1.5 sm:py-1 text-sm font-medium rounded-full whitespace-nowrap transition-colors ${
                      activeFilter === category.id 
                        ? 'bg-blue-100 text-blue-800' 
                        : 'text-gray-600 hover:bg-gray-100 active:bg-gray-200'
                    }`}
                  >
                    {category.name}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
        
        {/* Results Count */}
        <div className="mb-3 text-sm text-gray-600">
          Showing {filteredUploads.length} {filteredUploads.length === 1 ? 'file' : 'files'}
          {activeFilter !== 'all' && ` in ${FILE_CATEGORIES.find(c => c.id === activeFilter)?.name}`}
          {searchTerm && ` matching "${searchTerm}"`}
        </div>

        {error ? (
          <div className="bg-red-50 border-l-4 border-red-400 p-4 mb-6">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm text-red-700">{error}</p>
              </div>
            </div>
          </div>
        ) : filteredUploads.length === 0 ? (
          <div className="text-center py-12">
            <DocumentIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">
              {uploads.length === 0 
                ? 'No uploaded files' 
                : 'No files match your search'}
            </h3>
            <p className="mt-1 text-sm text-gray-500">
              {uploads.length === 0
                ? 'Upload your first file from the Dashboard.'
                : searchTerm
                  ? 'Try adjusting your search to find what you\'re looking for.'
                  : 'Try adjusting your filters to see more results.'}
            </p>
          </div>
        ) : (
          <div className="bg-white shadow overflow-hidden sm:rounded-md">
            <ul className="divide-y divide-gray-200">
              {filteredUploads.map((upload) => (
                <li key={upload.id} className="hover:bg-gray-50">
                  <div className="px-4 py-4 sm:px-6">
                    <div className="flex items-start justify-between">
                      <div className="flex items-start space-x-3 min-w-0 flex-1">
                        <div className="flex-shrink-0 mt-0.5">
                          {renderFileIcon(upload.mimeType)}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center justify-between">
                            <p className="text-base sm:text-sm font-medium text-gray-900 truncate pr-2">
                              {upload.fileName}
                            </p>
                            {getStatusBadge(upload)}
                          </div>
                          {upload.description && (
                            <p className="mt-1 text-sm text-gray-500 line-clamp-2">
                              {upload.description}
                            </p>
                          )}
                          <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-500">
                            <span className="inline-flex items-center px-2 py-0.5 rounded bg-gray-100 text-gray-800 font-medium">
                              {getCategoryLabel(upload.category)}
                            </span>
                            <span>{formatFileSize(upload.fileSize)}</span>
                            <span>•</span>
                            <span>{formatDate(upload.uploadedAt)}</span>
                            {upload.category === 'ComRCf' && upload.relatedParent && (
                              <>
                                <span>•</span>
                                <span className="inline-flex items-center px-2 py-0.5 rounded bg-blue-50 text-blue-700 font-medium">
                                  Ref: {upload.relatedParent}
                                </span>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="ml-2 flex-shrink-0">
                        <button
                          onClick={() => handleViewFile(upload)}
                          disabled={downloadingFile === upload.id}
                          className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-md disabled:opacity-50"
                          title="View file"
                        >
                          {downloadingFile === upload.id ? (
                            <div className="animate-spin h-4 w-4 border-2 border-gray-300 border-t-blue-600 rounded-full" />
                          ) : (
                            <>
                              <ArrowTopRightOnSquareIcon className="h-4 w-4 mr-1" />
                              View
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}
